import { NODES_BASE_KEY } from '@/api/nodes/helpers/base-key'
import { IInfiniteGetNodeContentQueryData } from '@/api/nodes/queries/get-node-content-query'
import { updateInfiniteNodesOptimistically } from '@/api/nodes/helpers/update-infinite-nodes-optimistically'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { QueryKey } from '@tanstack/react-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'

interface IDeleteDirectoryContentMutationParams {
    organizationId: string
    brandSlug: string

    selectedCount: number
    selectedDirectoryPath: string
}

type IDeleteDirectoryContentMutationPayload = Omit<
    IDeleteDirectoryContentMutationParams,
    'organizationId' | 'brandSlug'
>

// Actual payload which the backend expects
type IDeleteDirectoryContentApiPayload = {
    parentPath: string
}

export class DirectoryContentNodesMutation {
    static call(api: IApi, { organizationId, brandSlug, ...payload }: IDeleteDirectoryContentMutationParams) {
        return api.post<undefined, IDeleteDirectoryContentApiPayload>(
            `/organization/${organizationId}/brands/${brandSlug}/nodes/node/delete-content`,
            { parentPath: payload.selectedDirectoryPath }
        )
    }
}

interface IUseDeleteDirectoryContentMutationParams {
    nodeContentQueryKey: QueryKey
    sendToastSuccess: (count: number) => unknown
    sendToastError: (count: number) => unknown
    invalidateOnSuccess?: boolean
}

export const useDeleteDirectoryContentMutation = ({
    nodeContentQueryKey,
    sendToastError,
    sendToastSuccess,
}: IUseDeleteDirectoryContentMutationParams) => {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: (payload: IDeleteDirectoryContentMutationPayload) => {
            const promise = DirectoryContentNodesMutation.call(api, {
                ...payload,
                organizationId: apiParams.organizationId,
                brandSlug: apiParams.brandSlug,
            })

            let prev: IInfiniteGetNodeContentQueryData | undefined

            updateInfiniteNodesOptimistically({
                qc,
                selectedCount: payload.selectedCount,
                nodeContentQueryKey,
                onSuccess: sendToastSuccess,
                allFromDirectoryPath: payload.selectedDirectoryPath,
                filterPageData: (_n, _i, _a, { meta }) => (meta ? meta.path === payload.selectedDirectoryPath : false),

                assignPrev: (prevData) => {
                    prev = prevData
                },
            })

            return promise
                .then((result) => {
                    // Invalidation promise must be returned
                    return qc
                        .invalidateQueries({
                            predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                        })
                        .then(() => result)
                        .catch(() => result)
                })
                .catch((e) => {
                    sendToastError(payload.selectedCount)

                    qc.setQueryData<IInfiniteGetNodeContentQueryData>(nodeContentQueryKey, prev)
                    qc.refetchQueries({
                        predicate: (query) => NODES_BASE_KEY.every((key, i) => query.queryKey[i] === key),
                    })

                    throw e
                })
        },
    })
}
